import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DraggableAttributes,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CSSProperties, ReactNode, useEffect, useState } from 'react'

export interface DraggableItem {
  id: string | number
}

export interface DragHandleProps {
  attributes: DraggableAttributes
  listeners: ReturnType<typeof useSortable>['listeners']
  style: CSSProperties
}

export interface DraggableListProps<T extends DraggableItem> {
  items: T[]
  className?: string
  itemClassName?: string
  disabled?: boolean
  onReorder: (items: T[]) => void
  renderItem: (item: T, isDragging?: boolean, dragHandleProps?: DragHandleProps) => ReactNode
  onDragStart?: (event: DragStartEvent) => void
  onDragEnd?: (event: DragEndEvent) => void
}

interface SortableItemProps<T extends DraggableItem> {
  item: T
  itemClassName?: string
  disabled?: boolean
  renderItem: (item: T, isDragging?: boolean, dragHandleProps?: DragHandleProps) => ReactNode
}

const SortableItem = <T extends DraggableItem>({
  item,
  itemClassName,
  disabled = false,
  renderItem
}: SortableItemProps<T>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  const dragHandleProps: DragHandleProps = {
    attributes,
    listeners: disabled ? undefined : listeners,
    style: {
      cursor: disabled ? 'default' : 'grab',
      touchAction: 'none'
    }
  }

  return (
    <div ref={setNodeRef} style={style} className={itemClassName}>
      {renderItem(item, isDragging, dragHandleProps)}
    </div>
  )
}

const DraggableList = <T extends DraggableItem>({
  items,
  className = '',
  itemClassName = '',
  disabled = false,
  onDragStart: externalOnDragStart,
  onDragEnd: externalOnDragEnd,
  onReorder,
  renderItem
}: DraggableListProps<T>) => {
  const [activeId, setActiveId] = useState<string | number | null>(null)
  const [localItems, setLocalItems] = useState<T[]>(items)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    if (disabled) return
    const { active } = event
    setActiveId(active.id)
    externalOnDragStart?.(event)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    if (disabled) {
      setActiveId(null)
      return
    }

    const { active, over } = event

    if (!over) {
      setActiveId(null)
      externalOnDragEnd?.(event)
      return
    }

    if (active.id !== over.id) {
      const oldIndex = localItems.findIndex((item) => item.id === active.id)
      const newIndex = localItems.findIndex((item) => item.id === over.id)

      const newItems = arrayMove(localItems, oldIndex, newIndex)
      setLocalItems(newItems)
      onReorder(newItems)
    }

    setActiveId(null)
    externalOnDragEnd?.(event)
  }

  useEffect(() => {
    setLocalItems(items)
  }, [items])

  const activeItem = localItems.find((item) => item.id === activeId)

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={localItems.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={`flex flex-col gap-2 ${className}`}>
          {localItems.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              renderItem={renderItem}
              itemClassName={itemClassName}
              disabled={disabled}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay>
        {activeId && activeItem && !disabled ? (
          <div className="opacity-75 shadow-lg">{renderItem(activeItem, true)}</div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default DraggableList
